import { useEffect, useState, useCallback } from "react";
import {
  Paper, Box, Typography, TextField, Button, Stack, Alert, Chip, Divider,
  IconButton, CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import GavelIcon from "@mui/icons-material/Gavel";
import Swal from "sweetalert2";
import {
  getRecurso, salvarRecurso, listarAnexosRecursoCand,
  uploadAnexoCand, excluirAnexoCand, baixarAnexoCand,
} from "../../services/candidatoService";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");
const statusInfo = (s) =>
  s === "D" ? { label: "Deferido", color: "success" }
  : s === "I" ? { label: "Indeferido", color: "error" }
  : { label: "Em análise", color: "warning" };

export default function RecursoCandidato({ idFicha }) {
  const [cfg, setCfg] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [anexos, setAnexos] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const recurso = cfg?.Recurso || null;
  const idRecurso = recurso?.Id || null;
  const editavel = !!cfg?.NoPrazo && (!recurso || recurso.Status === "P");

  const carregar = useCallback(async () => {
    const { data } = await getRecurso(idFicha);
    setCfg(data);
    setDescricao(data.Recurso?.Descricao || "");
    if (data.Recurso?.Id) {
      try { setAnexos((await listarAnexosRecursoCand(data.Recurso.Id)).data); } catch { setAnexos([]); }
    } else setAnexos([]);
  }, [idFicha]);

  useEffect(() => { carregar().catch(() => setCfg({ Habilitado: false })); }, [carregar]);

  const registrar = async () => {
    if (!descricao.trim()) { Swal.fire("Atenção", "Descreva o motivo do recurso.", "warning"); return; }
    setSalvando(true);
    try {
      await salvarRecurso(idFicha, descricao.trim());
      await carregar();
      Swal.fire("Recurso registrado", "Seu recurso foi registrado. Anexe documentos em PDF, se necessário.", "success");
    } catch (e) {
      Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
    } finally { setSalvando(false); }
  };

  const anexar = async (files) => {
    if (!idRecurso) { Swal.fire("Atenção", "Registre o recurso antes de anexar arquivos.", "warning"); return; }
    try {
      for (const f of files) await uploadAnexoCand(idFicha, f, "Recurso", null, idRecurso);
      setAnexos((await listarAnexosRecursoCand(idRecurso)).data);
    } catch (e) {
      Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
    }
  };

  const remover = async (a) => {
    const r = await Swal.fire({ title: "Remover arquivo?", icon: "warning", showCancelButton: true, confirmButtonText: "Remover", cancelButtonText: "Cancelar" });
    if (!r.isConfirmed) return;
    try { await excluirAnexoCand(a.Id); setAnexos((await listarAnexosRecursoCand(idRecurso)).data); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error"); }
  };

  if (!cfg) return <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>;
  if (!cfg.Habilitado) return null;

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <GavelIcon color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Recurso contra o resultado</Typography>
        {recurso && <Chip size="small" {...statusInfo(recurso.Status)} label={statusInfo(recurso.Status).label} color={statusInfo(recurso.Status).color} />}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Período de recurso: {fmt(cfg.DataInicio)} a {fmt(cfg.DataFim)}
      </Typography>
      <Divider sx={{ my: 1.5 }} />

      {!cfg.NoPrazo && !recurso && (
        <Alert severity="info">O período para interposição de recurso não está aberto no momento.</Alert>
      )}

      {recurso && recurso.Status !== "P" && (
        <Alert severity={recurso.Status === "D" ? "success" : "error"} sx={{ mb: 2 }}>
          Recurso <b>{statusInfo(recurso.Status).label.toLowerCase()}</b>{recurso.DtAnalise ? ` em ${fmt(recurso.DtAnalise)}` : ""}.
          {recurso.ParecerAnalista ? <><br /><b>Parecer:</b> {recurso.ParecerAnalista}</> : null}
        </Alert>
      )}

      {(editavel || recurso) && (
        <TextField
          fullWidth multiline minRows={4} label="Descrição do recurso"
          placeholder="Explique o motivo do recurso contra o resultado."
          value={descricao} onChange={(e) => setDescricao(e.target.value)}
          disabled={!editavel} sx={{ mb: 2 }}
        />
      )}

      {editavel && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button variant="contained" onClick={registrar} disabled={salvando}>
            {recurso ? "Atualizar recurso" : "Registrar recurso"}
          </Button>
          {idRecurso && (
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
              Anexar PDF
              <input type="file" hidden multiple accept="application/pdf,.pdf"
                onChange={async (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; await anexar(fs); }} />
            </Button>
          )}
        </Stack>
      )}

      {anexos.length > 0 && (
        <Box>
          <Typography variant="subtitle2" color="primary">Anexos do recurso</Typography>
          <Divider sx={{ mb: 1, mt: 0.5 }} />
          {anexos.map((a) => (
            <Stack key={a.Id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1, wordBreak: "break-all" }}>{a.NomeOriginal}</Typography>
              <IconButton size="small" onClick={() => baixarAnexoCand(a.Id, a.NomeOriginal)}><DownloadIcon fontSize="small" /></IconButton>
              {editavel && <IconButton size="small" color="error" onClick={() => remover(a)}><DeleteIcon fontSize="small" /></IconButton>}
            </Stack>
          ))}
        </Box>
      )}
    </Paper>
  );
}
